import { useId, useState, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { startBackfill } from "@/api/ezyvet";
import { errorKind } from "@/api/errors";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { isoDay, quotaTone, tokenHealth, validateBackfill } from "@/components/ezyvet/health";
import { useEzyVetQuota, useNow, type QuotaReading } from "@/context/ezyvetQuota";
import { useToasts } from "@/hooks/useToast";
import { formatDuration } from "@/utils/ezyvetModels";
import type { ApiError } from "@/types";
import type { BackfillRequest, BackfillResponse } from "@/types/ezyvet";

interface Props {
  isAdmin: boolean;
}

/** ezyVet call budget, token health and (for admins) a historical backfill trigger. */
export function RateLimitWidget({ isAdmin }: Props) {
  const { quota, quotaAvailable, quotaError, connected, tokenExpiresAt } = useEzyVetQuota();
  const now = useNow(1000);

  return (
    <section className="card widget" aria-labelledby="ratelimit-title">
      <div className="row" style={{ justifyContent: "space-between" }}>
        <h2 id="ratelimit-title" style={{ margin: 0 }}>
          API quota &amp; sync
        </h2>
        <TokenBadge connected={connected} tokenExpiresAt={tokenExpiresAt} now={now} />
      </div>

      <QuotaMeter quota={quota} available={quotaAvailable} error={quotaError} now={now} />

      <dl className="kv kv--compact">
        <dt>Token expires in</dt>
        <dd className="mono">
          {tokenExpiresAt ? formatDuration((tokenExpiresAt - now) / 1000) : connected ? "No token" : "—"}
        </dd>
      </dl>

      {isAdmin && connected && <BackfillForm />}
    </section>
  );
}

// ---------------------------------------------------------------------------

function TokenBadge({ connected, tokenExpiresAt, now }: { connected: boolean; tokenExpiresAt: number | null; now: number }) {
  const { tone, label } = tokenHealth(connected, tokenExpiresAt, now);
  return (
    <StatusBadge tone={tone} title="Bearer token VetGuard uses for ezyVet calls">
      {label}
    </StatusBadge>
  );
}

function QuotaMeter({
  quota,
  available,
  error,
  now,
}: {
  quota: QuotaReading | null;
  available: boolean;
  error: ApiError | null;
  now: number;
}) {
  if (!quota) {
    return (
      <p className="muted" style={{ margin: "16px 0 0" }}>
        {!available
          ? "This server doesn't report ezyVet quota usage yet."
          : error
            ? `Quota unavailable: ${error.message}`
            : "Waiting for the first quota reading…"}
      </p>
    );
  }

  const { remaining, limit, resetAt } = quota;
  const resetIn = resetAt ? Math.max(0, (resetAt - now) / 1000) : null;
  const clamped = Math.max(0, Math.min(remaining, limit));
  const tone = quotaTone(clamped, limit);
  const exhausted = clamped <= 0 && (resetIn === null || resetIn > 0);

  return (
    <div style={{ marginTop: 16 }}>
      <div className="row" style={{ justifyContent: "space-between", marginBottom: 8 }}>
        <span>
          <b className="mono">{clamped}</b>
          <span className="muted"> / {limit} calls left this minute</span>
        </span>
        <span className="muted mono">{resetIn !== null ? (resetIn > 0 ? `resets in ${formatDuration(resetIn)}` : "window reset") : ""}</span>
      </div>
      <div
        className={`meter meter--${tone}`}
        role="progressbar"
        aria-label="ezyVet API calls remaining this minute"
        aria-valuemin={0}
        aria-valuemax={limit}
        aria-valuenow={clamped}
        aria-valuetext={`${clamped} of ${limit} calls remaining`}
      >
        <div className="meter-fill" style={{ width: `${limit > 0 ? (clamped / limit) * 100 : 0}%` }} />
      </div>
      {exhausted && (
        <div className="callout warning" role="status" style={{ marginTop: 12 }}>
          ezyVet&apos;s limit is used up. VetGuard pauses calls until the window resets, then carries on; nothing is lost.
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function BackfillForm() {
  const push = useToasts((s) => s.push);
  const id = useId();
  const today = isoDay(new Date());
  const [from, setFrom] = useState(() => isoDay(new Date(Date.now() - 30 * 86_400_000)));
  const [to, setTo] = useState(today);
  const [touched, setTouched] = useState(false);
  const problem = validateBackfill(from, to, today);

  const backfill = useMutation<BackfillResponse, ApiError, BackfillRequest>({
    mutationFn: startBackfill,
    onSuccess: (res) => push(`Backfill queued${res.job_id ? ` (job ${res.job_id.slice(0, 8)})` : ""}. Consults appear as they're audited.`, "success"),
    onError: (err) => {
      const kind = errorKind(err);
      push(
        kind === "not_found" || kind === "unavailable"
          ? "Backfill isn't available on this server yet."
          : kind === "rate_limited"
            ? "ezyVet's rate limit is reached; try the backfill again in a minute."
            : (err.message ?? "Backfill failed"),
        "error",
      );
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!problem) backfill.mutate({ from_date: from, to_date: to });
  };

  return (
    <form onSubmit={submit} className="subsection" noValidate>
      <h3>Backfill historical consults</h3>
      <p className="muted">
        Re-reads every consult in the range from ezyVet and audits it. It shares the 60-calls-a-minute budget, so large
        ranges take a while.
      </p>
      <div className="row" style={{ gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
        <div className="field" style={{ marginBottom: 0 }}>
          <label htmlFor={`${id}-from`}>From</label>
          <input id={`${id}-from`} type="date" value={from} max={today} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div className="field" style={{ marginBottom: 0 }}>
          <label htmlFor={`${id}-to`}>To</label>
          <input id={`${id}-to`} type="date" value={to} max={today} onChange={(e) => setTo(e.target.value)} />
        </div>
        <button type="submit" className="btn btn-sm" disabled={backfill.isPending}>
          {backfill.isPending ? "Queuing…" : "Queue backfill"}
        </button>
      </div>
      {touched && problem && (
        <span className="field-error" role="alert">
          {problem}
        </span>
      )}
    </form>
  );
}
